import moment from "moment";
import {
  baseURLImage,
  capitalizeFirstLetter,
  getNameProfile,
  parseBooleanVal,
} from "Utils/utils";
import {
  isAdvanceSameClockTimeFullDay,
  isAdvanceUnallocatedSameClockSegment,
} from "./advanceModeValidation";

export const formatSilHomeLabel = (home) => {
  if (!home) return "";
  if (home.label) return home.label;
  const street = capitalizeFirstLetter(home?.street_address || "");
  const suburb = capitalizeFirstLetter(home?.suburb || "");
  const state = home?.state?.name?.toUpperCase() || "";
  const postal = home?.postal_code || "";
  if (street || suburb) {
    return `${street}${street && suburb ? ", " : ""}${suburb}${
      state || postal ? `, ${state} ${postal}`.trim() : ""
    }`.trim();
  }
  return suburb || street || "Home";
};

/** True when a participant is eligible for advance-shift dropdowns. */
export const isActiveParticipantStatus = (item) => {
  if (item == null) return false;
  if (item.status === undefined || item.status === null || item.status === "") {
    return true;
  }
  if (item.status === true || item.status === 1) return true;
  if (item.status === false || item.status === 0) return false;
  const normalized = String(item.status).trim().toLowerCase();
  return normalized === "active" || normalized === "true" || normalized === "1";
};

/** True when a worker is eligible for advance-shift dropdowns. */
export const isActiveWorkerStatus = (item) => {
  if (item == null) return false;
  if (item.status === undefined || item.status === null || item.status === "") {
    return true;
  }
  if (item.status === true || item.status === 1) return true;
  if (item.status === false || item.status === 0) return false;
  const normalized = String(item.status).trim().toLowerCase();
  return (
    normalized === "active" ||
    normalized === "true" ||
    normalized === "1"
  );
};

/** Map V5 sil-home / common-resources participant payload to multi-select option shape. */
export const mapSilHomeParticipantToOption = (participant) => {
  if (!participant) return null;
  const label =
    `${participant.first_name || ""} ${participant.last_name || ""}`.trim() ||
    "Participant";
  const uuid = participant.uuid;
  const icon = participant.profile_picture
    ? baseURLImage(participant.profile_picture)
    : getNameProfile(participant.first_name, participant.last_name || " ");
  return {
    ...participant,
    id: participant.id,
    uuid,
    value: uuid,
    label,
    first_name: participant.first_name,
    last_name: participant.last_name,
    profile_picture: participant.profile_picture || icon,
    icon,
  };
};

/** Map V5 common-resources worker payload to multi-select option shape. */
export const mapCommonWorkerToOption = (worker) => {
  if (!worker) return null;
  const label =
    `${worker.first_name || ""} ${worker.last_name || ""}`.trim() || "Worker";
  const uuid = worker.uuid;
  return {
    ...worker,
    id: worker.id,
    uuid,
    value: uuid,
    label,
    first_name: worker.first_name,
    last_name: worker.last_name,
    icon: worker.profile_picture
      ? baseURLImage(worker.profile_picture)
      : getNameProfile(worker.first_name, worker.last_name || " "),
  };
};

export const extractCommonResourceRecords = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const ADVANCE_COMMON_WORKER_GROUP_LABEL = "Common Workers";
export const ADVANCE_OTHER_PARTICIPANT_WORKER_GROUP_LABEL =
  "Other Participant Workers";

/** Resolve nested common-resources payload (supports wrapped `data` key). */
export const resolveCommonResourcePayload = (response) => {
  const payload = response?.data ?? response;
  if (payload?.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

/** Participant anchor: common_worker + all_participant_worker lists from V5 API. */
export const extractCommonResourceWorkerLists = (response) => {
  const payload = resolveCommonResourcePayload(response);
  const commonWorkers = Array.isArray(payload?.common_worker)
    ? payload.common_worker
    : Array.isArray(payload?.records)
    ? payload.records
    : [];
  const allParticipantWorkers = Array.isArray(payload?.all_participant_worker)
    ? payload.all_participant_worker
    : [];

  return {
    commonWorkers: commonWorkers.filter(isActiveWorkerStatus),
    allParticipantWorkers: allParticipantWorkers.filter(isActiveWorkerStatus),
  };
};

export const buildGroupedCommonWorkerOptions = (
  commonWorkers = [],
  allParticipantWorkers = [],
  sortWorkers = (items) => items
) => {
  const commonOptions = sortWorkers(
    commonWorkers.map(mapCommonWorkerToOption).filter(Boolean)
  );
  const otherOptions = sortWorkers(
    allParticipantWorkers.map(mapCommonWorkerToOption).filter(Boolean)
  );

  const groups = [];
  if (commonOptions.length) {
    groups.push({
      label: ADVANCE_COMMON_WORKER_GROUP_LABEL,
      options: commonOptions,
    });
  }
  if (otherOptions.length) {
    groups.push({
      label: ADVANCE_OTHER_PARTICIPANT_WORKER_GROUP_LABEL,
      options: otherOptions,
    });
  }
  return groups;
};

export const flattenGroupedWorkerOptions = (groupedOptions = []) => {
  if (!Array.isArray(groupedOptions) || !groupedOptions.length) return [];
  if (groupedOptions[0]?.options) {
    return groupedOptions.flatMap((group) => group.options || []);
  }
  return groupedOptions;
};

/** Merge distance-enhanced flat workers back into grouped react-select options. */
export const applyFlatWorkersToGroupedOptions = (
  groupedOptions = [],
  flatWorkers = []
) => {
  if (!groupedOptions?.length) return groupedOptions;
  const byUuid = new Map(
    (flatWorkers || []).map((worker) => [worker.uuid || worker.value, worker])
  );

  return groupedOptions
    .map((group) => ({
      ...group,
      options: (group.options || []).map(
        (option) => byUuid.get(option.uuid || option.value) || option
      ),
    }))
    .filter((group) => group.options?.length);
};

/** Keep shift-assigned workers visible in grouped dropdown when absent from common-resources. */
export const mergeAssignedWorkersIntoGroupedOptions = (
  groupedOptions = [],
  assignedWorkers = [],
  sortWorkers = (items) => items
) => {
  if (!assignedWorkers?.length) return groupedOptions;

  const flat = flattenGroupedWorkerOptions(groupedOptions);
  const flatUuids = new Set(flat.map((w) => getWorkerUuid(w)).filter(Boolean));
  const flatIds = new Set(
    flat.map((w) => (w?.id != null ? String(w.id) : null)).filter(Boolean)
  );
  const missing = assignedWorkers
    .map((worker) => mapCommonWorkerToOption(worker) || worker)
    .filter((worker) => {
      // Don't re-add draft stubs when the real worker is already present by id
      if (worker?.id != null && flatIds.has(String(worker.id))) return false;
      const uuid = getWorkerUuid(worker);
      return uuid && !flatUuids.has(uuid);
    });

  if (!missing.length) return groupedOptions;

  const merged = groupedOptions.map((group) => ({
    ...group,
    options: [...(group.options || [])],
  }));

  let otherGroup = merged.find(
    (group) => group.label === ADVANCE_OTHER_PARTICIPANT_WORKER_GROUP_LABEL
  );
  if (!otherGroup) {
    otherGroup = {
      label: ADVANCE_OTHER_PARTICIPANT_WORKER_GROUP_LABEL,
      options: [],
    };
    merged.push(otherGroup);
  }

  sortWorkers(missing).forEach((worker) => {
    const uuid = getWorkerUuid(worker);
    if (!otherGroup.options.some((option) => getWorkerUuid(option) === uuid)) {
      otherGroup.options.push(worker);
    }
  });

  return merged.filter((group) => group.options?.length);
};

export const ADVANCE_RATIO_SIDE_MAX = 10;
export const DEFAULT_ADVANCE_RATIO = "1:1";

const buildAdvanceRatioOption = (left, right) => {
  const value = `${left}:${right}`;
  return { label: value, value };
};

/** Flat 1:1 … 10:10 list for lookups (legacy 1:N saved values still display). */
export const ADVANCE_RATIO_OPTIONS = Array.from(
  { length: ADVANCE_RATIO_SIDE_MAX },
  (_, i) =>
    Array.from({ length: ADVANCE_RATIO_SIDE_MAX }, (_, j) =>
      buildAdvanceRatioOption(i + 1, j + 1)
    )
).flat();

export const formatAdvanceRatio = (left, right) => `${left}:${right}`;

export const parseAdvanceRatioParts = (ratio) => {
  const parts = String(ratio || DEFAULT_ADVANCE_RATIO).split(":");
  const left = parseInt(parts[0], 10);
  const right = parseInt(parts[1], 10);
  return {
    left: Number.isNaN(left) || left < 1 ? 1 : left,
    right: Number.isNaN(right) || right < 1 ? 1 : right,
  };
};

export const findAdvanceRatioOption = (ratio) => {
  const value = ratio || DEFAULT_ADVANCE_RATIO;
  return (
    ADVANCE_RATIO_OPTIONS.find((option) => option.value === value) || {
      label: value,
      value,
    }
  );
};

export const extractRatioFromLineItemLabel = (label) => {
  const text = String(label || "");
  const bothSides = text.match(/\b([1-9]|10):([1-9]|1[0-9]|20)\b/);
  if (bothSides) return bothSides[0];
  const oneToN = text.match(/\b1:\d+\b/);
  return oneToN ? oneToN[0] : null;
};

/** Map shift_ndis_items API rows to advance line-item select options. */
export const mapShiftNdisItemsToOptions = (items = [], { isSta = false } = {}) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const isCustom = Boolean(item?.is_custom || item?.custom_line_item_uuid);
    const number = item.support_item_number || "";
    const rawName = item.support_item_name || "";
    const name = isCustom ? [number, rawName].filter(Boolean).join(" ") : rawName;
    const uuid = item?.custom_line_item_uuid || null;
    const ratio = extractRatioFromLineItemLabel(rawName) || "1:1";
    return {
      label: name,
      value: isCustom ? uuid : item.id,
      number,
      name,
      price: item.price,
      ratio,
      is_custom: isCustom,
      uuid,
      funding_type: item?.funding_type || null,
      invoice_service_type_id: item?.invoice_service_type_id ?? null,
      __type: isCustom ? "custom" : "ndis",
      ...(isSta ? { is_sta_line_item: true } : {}),
    };
  });

/**
 * A support item is a sleepover claim when its name carries "Sleepover".
 * Mirrors the backend catalogue filter (`support_item_name LIKE '%Sleepover%'`)
 * used by shiftNdisItems / TimeSheetTraits, so both sides agree on what counts.
 */
export const isSleepoverLineItemName = (name) =>
  String(name || "")
    .toLowerCase()
    .includes("sleepover");

/**
 * Sleepover flag for one advance billing segment.
 *
 * A persisted value wins so a manual un-toggle from Code Allocation survives a
 * re-save from the advance form; otherwise it is derived from the selected line
 * item's name.
 */
export const resolveAdvanceSplitSleepover = (split = {}) => {
  if (split?.is_sleepover !== undefined && split?.is_sleepover !== null) {
    return parseBooleanVal(split.is_sleepover);
  }
  return isSleepoverLineItemName(split?.support_item_name);
};

export const findStaLineItemOption = (number, options = []) => {
  if (!number) return null;
  return (
    options.find(
      (opt) =>
        opt.number === number || String(opt.value) === String(number)
    ) || null
  );
};

export const ADVANCE_STA_SERVICE_CATEGORY_LABEL = "Assistance with Daily Life";

export const findStaServiceCategoryOption = (serviceTypes = []) => {
  const target = ADVANCE_STA_SERVICE_CATEGORY_LABEL.toLowerCase();
  return (
    serviceTypes.find(
      (item) =>
        String(item.label || item.name || "")
          .trim()
          .toLowerCase() === target
    ) ||
    serviceTypes.find((item) =>
      String(item.label || item.name || "")
        .toLowerCase()
        .includes("assistance with daily life")
    ) ||
    null
  );
};

export const applyStaServiceCategoryToAllSplits = (
  splits = [],
  serviceTypes = [],
  setValue
) => {
  const option = findStaServiceCategoryOption(serviceTypes);
  if (!option || !setValue) return;
  splits.forEach((_, index) => {
    setValue(`advance_splits.${index}.invoice_service_type_id`, option.value ?? null, {
      shouldDirty: true,
    });
  });
};

/** Apply STA service category to every segment for one participant. */
export const applyStaServiceCategoryToParticipantSplits = (
  participantUuid,
  splits = [],
  serviceTypes = [],
  setValue
) => {
  const option = findStaServiceCategoryOption(serviceTypes);
  if (!option || !setValue || !participantUuid) return;
  splits.forEach((split, index) => {
    if (getParticipantUuid(getSplitParticipant(split)) !== participantUuid) return;
    setValue(`advance_splits.${index}.invoice_service_type_id`, option.value ?? null, {
      shouldDirty: true,
    });
  });
};

/** Claim type options for schedule / advance splits (frontend); API still uses `is_travel`. */
export const ADVANCE_CLAIM_TYPE_OPTIONS = [
  { label: "STAND", value: "STAND" },
  { label: "NF2F", value: "NF2F" },
  { label: "Travel", value: "Travel" },
];

export const DEFAULT_ADVANCE_CLAIM_TYPE = "STAND";

/** STA shifts use a full 24-hour window (12:00 AM – 12:00 AM). */
export const ADVANCE_STA_FULL_DAY_TIME = "00:00";

export const showClaimTypeInScheduleTimesheets = (spRosteringSetting) => {
  const s =
    spRosteringSetting?.show_claim_type_schedule_timesheets ??
    spRosteringSetting?.show_travel_toggle_schedule_timesheets;
  return (
    parseBooleanVal(s) ||
    s === 1 ||
    s === "1"
  );
};

export const getBreakdownClaimType = (row) => {
  if (row?.claim_type) return row.claim_type;
  if (
    parseBooleanVal(row?.is_travel) ||
    row?.is_travel === 1 ||
    String(row?.is_travel) === "1"
  ) {
    return "Travel";
  }
  return null;
};

export const formatBreakdownClaimTypeSuffix = (row) => {
  const claimType = getBreakdownClaimType(row);
  if (!claimType || claimType === DEFAULT_ADVANCE_CLAIM_TYPE) return "";
  return ` - ${claimType}`;
};

export const claimTypeFromIsTravel = (isTravel) =>
  parseBooleanVal(isTravel) ||
  isTravel === 1 ||
  String(isTravel) === "1"
    ? "Travel"
    : DEFAULT_ADVANCE_CLAIM_TYPE;

export const isTravelFromClaimType = (claimType) => claimType === "Travel";

const stripClaimTypeSuffixFromLabel = (text) =>
  String(text ?? "")
    .replace(/\s*-?\s*travel\s*$/i, "")
    .replace(/\s*-?\s*nf2f\s*$/i, "")
    .replace(/\s*-?\s*stand\s*$/i, "")
    .trim();

/** Append claim-type suffix to NDIS line item labels in advance mode. */
export const mapNdisOptionsForClaimType = (options, claimType) => {
  if (!options?.length || !claimType) {
    return options;
  }

  const normalizedClaimType =
    claimType === DEFAULT_ADVANCE_CLAIM_TYPE ? DEFAULT_ADVANCE_CLAIM_TYPE : claimType;

  if (normalizedClaimType === DEFAULT_ADVANCE_CLAIM_TYPE) {
    return options.map((opt) => {
      const raw = stripClaimTypeSuffixFromLabel(opt.name ?? opt.label ?? "");
      const base = capitalizeFirstLetter(raw);
      return { ...opt, label: base };
    });
  }

  const suffixByClaimType = {
    Travel: "Travel",
    NF2F: "NF2F",
  };
  const suffix = suffixByClaimType[normalizedClaimType];
  if (!suffix) return options;

  return options.map((opt) => {
    const raw = stripClaimTypeSuffixFromLabel(opt.name ?? opt.label ?? "");
    const base = capitalizeFirstLetter(raw);
    return { ...opt, label: `${base} - ${suffix}` };
  });
};

/**
 * Map frontend split row to API shape. The V5 backend persists `claim_type`
 * directly, so it must be kept (older code stripped it, which lost the value on
 * save). `is_travel` is still emitted for backward compatibility.
 */
export const mapSplitRowClaimTypeForApi = (split = {}) => {
  const claimType = split?.claim_type ?? claimTypeFromIsTravel(split?.is_travel);
  return {
    ...split,
    claim_type: claimType,
    is_travel: isTravelFromClaimType(claimType),
  };
};

export const getParticipantUuid = (participant) => {
  if (participant == null) return null;
  if (typeof participant === "string") return participant;
  return participant?.uuid || participant?.value || null;
};

/** Draft resume stubs use `Participant #<id>` when the list was not loaded yet. */
export const isStubParticipantOption = (participant) => {
  if (participant == null || typeof participant !== "object") return false;
  const label = String(participant.label || "").trim();
  if (/^Participant #\d+$/i.test(label)) return true;
  const first = String(participant.first_name || "").trim();
  const last = String(participant.last_name || "").trim();
  return first === "Participant" && /^#\d+$/.test(last);
};

/** Match participants by UUID/value or numeric id (draft stubs only have numeric id). */
export const participantsMatchIdentity = (a, b) => {
  if (a == null || b == null) return false;
  const aUuid = getParticipantUuid(a);
  const bUuid = getParticipantUuid(b);
  if (aUuid && bUuid && String(aUuid) === String(bUuid)) return true;
  if (a?.id != null && b?.id != null && String(a.id) === String(b.id)) {
    return true;
  }
  return false;
};

/**
 * Prefer a full option from the loaded participant list over a draft stub.
 */
export const resolveParticipantAgainstOptions = (
  participant,
  participantOptions = []
) => {
  if (participant == null || participant === "") return null;
  const list = Array.isArray(participantOptions) ? participantOptions : [];
  if (!list.length) {
    return typeof participant === "object" ? participant : null;
  }

  const uuidFrom =
    typeof participant === "string" || typeof participant === "number"
      ? String(participant)
      : participant?.uuid || participant?.value || null;
  const idFrom =
    typeof participant === "object" && participant?.id != null
      ? participant.id
      : null;

  const matches = list.filter((p) => {
    if (!p) return false;
    if (
      uuidFrom &&
      (String(p.uuid) === String(uuidFrom) ||
        String(p.value) === String(uuidFrom) ||
        String(p.id) === String(uuidFrom))
    ) {
      return true;
    }
    if (idFrom != null && String(p.id) === String(idFrom)) return true;
    return false;
  });

  const match =
    matches.find((p) => !isStubParticipantOption(p)) || matches[0] || null;

  if (match) return mapSilHomeParticipantToOption(match) || match;
  return typeof participant === "object" ? participant : null;
};

/** Numeric participant id for NDIS line-items API (pt_id query param). */
export const getParticipantId = (participant, participantOptions = []) => {
  if (participant == null) return null;
  if (typeof participant === "number") return participant;
  if (typeof participant === "string" && /^\d+$/.test(participant)) {
    return Number(participant);
  }
  if (participant.id != null && participant.id !== "") {
    return participant.id;
  }
  if (participant.pt_id != null && participant.pt_id !== "") {
    return participant.pt_id;
  }
  const uuid = getParticipantUuid(participant);
  if (!uuid || !participantOptions?.length) return null;
  const match = participantOptions.find(
    (p) =>
      p.uuid === uuid ||
      p.value === uuid ||
      String(p.id) === String(uuid)
  );
  return match?.id ?? null;
};

/** Numeric worker id for overlap / availability APIs. */
export const getWorkerId = (worker, workerOptions = []) => {
  if (worker == null) return null;
  if (typeof worker === "number") return worker;
  if (typeof worker === "string" && /^\d+$/.test(worker)) {
    return Number(worker);
  }
  if (worker.id != null && worker.id !== "") {
    return worker.id;
  }
  const uuid = getWorkerUuid(worker);
  if (!uuid || !workerOptions?.length) return null;
  const match = workerOptions.find(
    (w) =>
      w.uuid === uuid ||
      w.value === uuid ||
      String(w.id) === String(uuid)
  );
  return match?.id ?? null;
};

export const resolveAdvanceWorkerIds = (selectedWorkers = [], workerOptions = []) =>
  [
    ...new Set(
      (selectedWorkers || [])
        .map((w) => getWorkerId(w, workerOptions))
        .filter(Boolean)
    ),
  ];

export const resolveAdvanceParticipantIds = (
  selectedParticipants = [],
  participantOptions = []
) =>
  [
    ...new Set(
      (selectedParticipants || [])
        .map((p) => getParticipantId(p, participantOptions))
        .filter(Boolean)
    ),
  ];

/** Normalize shift_date for shift_ndis_items API (supports YYYY-MM-DD and DD-MM-YYYY). */
export const formatShiftDateForNdisApi = (shiftDate) => {
  if (!shiftDate) return "";
  const parsed = moment(shiftDate, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true);
  if (!parsed.isValid()) return "";
  return moment.utc(parsed.format("YYYY-MM-DD"), "YYYY-MM-DD").toISOString();
};

/** Map pt_id (uuid string or partial object) to multi-select option shape. */
export const resolveParticipantSelection = (
  pt,
  participantOptions = [],
  userItem = null
) => {
  if (pt == null || pt === "") return null;

  const uuidFrom =
    typeof pt === "string"
      ? pt
      : pt?.uuid || pt?.value || null;

  const fromOptions = resolveParticipantAgainstOptions(pt, participantOptions);

  if (fromOptions && !isStubParticipantOption(fromOptions)) return fromOptions;

  if (
    userItem &&
    uuidFrom &&
    (userItem.uuid === uuidFrom || userItem.value === uuidFrom)
  ) {
    const label =
      userItem.label ||
      `${userItem.first_name || ""} ${userItem.last_name || ""}`.trim();
    return {
      ...userItem,
      label: label || "Participant",
      value: userItem.uuid || userItem.value,
      uuid: userItem.uuid || userItem.value,
    };
  }

  if (typeof pt === "object" && pt !== null) {
    const label =
      pt.label ||
      `${pt.first_name || ""} ${pt.last_name || ""}`.trim();
    if (!label && !uuidFrom) return null;
    return {
      ...pt,
      label: label || "Participant",
      value: pt.value || pt.uuid,
      uuid: pt.uuid || pt.value,
    };
  }

  return null;
};

export const resolveParticipantsList = (
  pt,
  participantOptions = [],
  userItem = null
) => {
  if (!pt) return [];
  const list = Array.isArray(pt) ? pt.filter(Boolean) : [pt];
  return list
    .map((item) => resolveParticipantSelection(item, participantOptions, userItem))
    .filter(Boolean);
};

export const participantNeedsResolve = (participant) => {
  if (participant == null) return false;
  if (typeof participant === "string") return true;
  // Draft stubs look complete (they have a label) but must be upgraded once options load
  if (isStubParticipantOption(participant)) return true;
  if (participant.label) return false;
  if (participant.first_name || participant.last_name) return false;
  return true;
};

export const getParticipantDisplayName = (participant) => {
  if (!participant) return "Participant";
  if (participant.label) return participant.label;
  const name = `${participant.first_name || ""} ${participant.last_name || ""}`.trim();
  return name || "Participant";
};

/** Resolved STA flag (default false). */
export const resolveStaValue = (value) =>
  parseBooleanVal(value) || value === 1 || String(value) === "1";

/** One billing row for a single participant (extra rows via Split on that participant). */
export const createEmptyParticipantSplit = (participant = null, watchFormData = {}) => ({
  participant: participant || null,
  participants: participant ? [participant] : [],
  participant_start_time: watchFormData?.start_time || "",
  participant_end_time: watchFormData?.end_time || "",
  invoice_service_type_id:
    watchFormData?.invoice_service_type_id != null &&
    watchFormData?.invoice_service_type_id !== ""
      ? watchFormData.invoice_service_type_id
      : null,
  invoice_item_id: null,
  custom_line_item_uuid: null,
  support_item_number: "",
  support_item_name: "",
  ratio: "1:1",
  km_for_invoicing:
    watchFormData?.km_for_invoicing !== undefined
      ? resolveKmForInvoicingValue(watchFormData.km_for_invoicing)
      : true,
  default_km_code:
    watchFormData?.default_km_code !== undefined
      ? watchFormData.default_km_code
      : null,
  claim_type: DEFAULT_ADVANCE_CLAIM_TYPE,
  sta: resolveStaValue(watchFormData?.sta),
});

export const createEmptyClientSplit = createEmptyParticipantSplit;

/** Resolved km_for_invoicing flag (default OFF / false). */
export const resolveKmForInvoicingValue = (value) => {
  if (value === undefined || value === null) return false;
  return (
    parseBooleanVal(value) || value === 1 || String(value) === "1"
  );
};

/** km_for_invoicing for a participant across all their segments (uses first segment). */
export const getParticipantKmForInvoicing = (participantUuid, allSplits = []) => {
  if (!participantUuid) return false;
  const match = allSplits.find(
    (s) => getParticipantUuid(getSplitParticipant(s)) === participantUuid
  );
  return resolveKmForInvoicingValue(match?.km_for_invoicing);
};

/**
 * Overlay form/API `participant_travel` onto advance split rows so multi-segment
 * participants stay consistent (prefer travel rows over last-segment-only).
 */
export const applyParticipantTravelToSplits = (
  splits = [],
  participantTravel = []
) => {
  if (!Array.isArray(splits) || !splits.length) return splits;
  if (!Array.isArray(participantTravel) || !participantTravel.length) {
    return splits;
  }

  const byId = new Map();
  const byUuid = new Map();
  participantTravel.forEach((row) => {
    if (row?.participant_id != null) {
      byId.set(String(row.participant_id), row);
    }
    if (row?.participant_uuid) {
      byUuid.set(String(row.participant_uuid), row);
    }
  });

  return splits.map((split) => {
    const pt = getSplitParticipant(split);
    const travel =
      (pt?.id != null && byId.get(String(pt.id))) ||
      (pt?.uuid && byUuid.get(String(pt.uuid))) ||
      null;
    if (!travel) return split;
    return {
      ...split,
      km_for_invoicing: resolveKmForInvoicingValue(
        travel.km_for_invoicing ?? travel.apply_km
      ),
      default_km_code:
        travel.default_km_code !== undefined
          ? travel.default_km_code
          : split.default_km_code ?? null,
    };
  });
};

/** Build `participant_travel[]` for V5 advance create/update (one row per participant). */
export const buildParticipantTravelFromSplits = (
  advanceSplits = [],
  participantOptions = []
) => {
  const seen = new Set();
  const rows = [];

  (advanceSplits || []).forEach((split) => {
    const pt = getSplitParticipant(split);
    const participantId = getParticipantId(pt, participantOptions);
    if (!participantId || seen.has(String(participantId))) return;
    seen.add(String(participantId));
    rows.push({
      participant_id: Number(participantId),
      km_for_invoicing: resolveKmForInvoicingValue(split.km_for_invoicing),
      default_km_code:
        split.default_km_code !== undefined ? split.default_km_code : null,
    });
  });

  return rows;
};

/** Set km_for_invoicing on every segment for the same participant. */
export const setParticipantKmForInvoicing = (
  participantUuid,
  kmForInvoicing,
  allSplits = [],
  setValue
) => {
  if (!participantUuid || !setValue) return;
  allSplits.forEach((s, i) => {
    if (getParticipantUuid(getSplitParticipant(s)) === participantUuid) {
      setValue(`advance_splits.${i}.km_for_invoicing`, kmForInvoicing, {
        shouldDirty: true,
      });
    }
  });
};

/** default_km_code for a participant across all their segments (uses first segment). */
export const getParticipantDefaultKmCode = (participantUuid, allSplits = []) => {
  if (!participantUuid) return null;
  const match = allSplits.find(
    (s) => getParticipantUuid(getSplitParticipant(s)) === participantUuid
  );
  return match?.default_km_code !== undefined ? match.default_km_code : null;
};

/** Set default_km_code on every segment for the same participant. */
export const setParticipantDefaultKmCode = (
  participantUuid,
  defaultKmCode,
  allSplits = [],
  setValue
) => {
  if (!participantUuid || !setValue) return;
  allSplits.forEach((s, i) => {
    if (getParticipantUuid(getSplitParticipant(s)) === participantUuid) {
      setValue(`advance_splits.${i}.default_km_code`, defaultKmCode, {
        shouldDirty: true,
      });
    }
  });
};

/** STA for a participant across all their segments (uses first segment). */
export const getParticipantSta = (participantUuid, allSplits = []) => {
  if (!participantUuid) return false;
  const match = allSplits.find(
    (s) => getParticipantUuid(getSplitParticipant(s)) === participantUuid
  );
  return resolveStaValue(match?.sta);
};

/** Set STA on every segment for the same participant. */
export const setParticipantSta = (
  participantUuid,
  sta,
  allSplits = [],
  setValue
) => {
  if (!participantUuid || !setValue) return;
  allSplits.forEach((s, i) => {
    if (getParticipantUuid(getSplitParticipant(s)) === participantUuid) {
      setValue(`advance_splits.${i}.sta`, resolveStaValue(sta), {
        shouldDirty: true,
      });
    }
  });
};

/** First selected participant's primary segment — STA default template source. */
export const getFirstParticipantStaTemplate = (
  splits = [],
  selectedParticipants = []
) => {
  if (!selectedParticipants?.length || !splits?.length) return null;

  for (const participant of selectedParticipants) {
    const uuid = getParticipantUuid(participant);
    if (!uuid) continue;
    const index = splits.findIndex(
      (split) => getParticipantUuid(getSplitParticipant(split)) === uuid
    );
    if (index >= 0) {
      return { index, uuid, split: splits[index] };
    }
  }

  return null;
};

/** Billing + time fields copied from the first participant when STA is enabled. */
export const getStaTemplateFieldsFromSplit = (split = {}) => ({
  participant_start_time: split.participant_start_time ?? "",
  participant_end_time: split.participant_end_time ?? "",
  invoice_service_type_id: split.invoice_service_type_id ?? null,
  invoice_item_id: split.invoice_item_id ?? null,
  custom_line_item_uuid: split.custom_line_item_uuid ?? null,
  support_item_number: split.support_item_number ?? "",
  support_item_name: split.support_item_name ?? "",
  ratio: split.ratio || "1:1",
  claim_type: split.claim_type ?? null,
});

export const applyStaTemplateFieldsToSplitIndex = (
  index,
  templateFields,
  setValue,
  { includeTimes = true, includeBilling = true } = {}
) => {
  if (!setValue || index == null || !templateFields) return;
  const base = `advance_splits.${index}`;

  if (includeTimes) {
    setValue(`${base}.participant_start_time`, templateFields.participant_start_time ?? "", {
      shouldDirty: true,
    });
    setValue(`${base}.participant_end_time`, templateFields.participant_end_time ?? "", {
      shouldDirty: true,
    });
  }

  if (includeBilling) {
    setValue(`${base}.invoice_service_type_id`, templateFields.invoice_service_type_id ?? null, {
      shouldDirty: true,
    });
    setValue(`${base}.invoice_item_id`, templateFields.invoice_item_id ?? null, {
      shouldDirty: true,
    });
    setValue(`${base}.custom_line_item_uuid`, templateFields.custom_line_item_uuid ?? null, {
      shouldDirty: true,
    });
    setValue(`${base}.support_item_number`, templateFields.support_item_number ?? "", {
      shouldDirty: true,
    });
    setValue(`${base}.support_item_name`, templateFields.support_item_name ?? "", {
      shouldDirty: true,
    });
    setValue(`${base}.ratio`, templateFields.ratio || "1:1", { shouldDirty: true });
    setValue(`${base}.claim_type`, templateFields.claim_type ?? null, {
      shouldDirty: true,
    });
  }
};

/**
 * Copy the first participant's primary segment onto other participants when STA
 * is on. Skips the template participant; only updates each participant's first
 * segment. Use `onlyEmptyLineItems` when the first participant picks a line item
 * so participants who already chose their own item are left unchanged.
 */
export const propagateStaDefaultsFromFirstParticipant = ({
  splits = [],
  selectedParticipants = [],
  setValue,
  targetParticipantUuids = null,
  onlyEmptyLineItems = false,
  includeTimes = true,
  includeBilling = true,
} = {}) => {
  if (!setValue) return;

  const template = getFirstParticipantStaTemplate(splits, selectedParticipants);
  if (!template?.split) return;

  const templateFields = getStaTemplateFieldsFromSplit(template.split);
  const firstUuid = template.uuid;
  const firstSegmentIndexByUuid = new Map();

  splits.forEach((split, index) => {
    const uuid = getParticipantUuid(getSplitParticipant(split));
    if (!uuid || uuid === firstUuid) return;
    if (
      Array.isArray(targetParticipantUuids) &&
      targetParticipantUuids.length > 0 &&
      !targetParticipantUuids.includes(uuid)
    ) {
      return;
    }
    if (!firstSegmentIndexByUuid.has(uuid)) {
      firstSegmentIndexByUuid.set(uuid, index);
    }
  });

  firstSegmentIndexByUuid.forEach((index) => {
    const split = splits[index];
    if (onlyEmptyLineItems && includeBilling) {
      const hasLineItem =
        split?.invoice_item_id ||
        split?.support_item_number ||
        split?.support_item_name;
      if (hasLineItem) return;
    }

    applyStaTemplateFieldsToSplitIndex(index, templateFields, setValue, {
      includeTimes,
      includeBilling,
    });
  });
};

/** Merge STA template fields into a new participant split row (before form replace). */
export const mergeStaTemplateIntoParticipantSplit = (
  row = {},
  splits = [],
  selectedParticipants = []
) => {
  const template = getFirstParticipantStaTemplate(splits, selectedParticipants);
  if (!template?.split) return row;
  return {
    ...row,
    ...getStaTemplateFieldsFromSplit(template.split),
  };
};

/**
 * Copy billing fields from an existing segment when splitting (same participant).
 * New segment start defaults to the previous segment's end; end defaults to the
 * overall shift end. Existing segment times are never rewritten here.
 * When the previous segment already ends at the shift end, the new row is an
 * unallocated same-clock stub (0h until the user sets times) — not a 24h day.
 */
export const cloneParticipantSplitForSegmentSplit = (
  sourceSplit = {},
  participant = null,
  shiftTimes = {}
) => {
  const claimType =
    sourceSplit.claim_type ??
    (sourceSplit.is_travel !== undefined && sourceSplit.is_travel !== null
      ? claimTypeFromIsTravel(sourceSplit.is_travel)
      : null);

  return {
    ...createEmptyParticipantSplit(participant, shiftTimes),
    participant: participant || sourceSplit.participant || null,
    participants: participant
      ? [participant]
      : sourceSplit.participants || [],
    participant_start_time:
      sourceSplit.participant_end_time ||
      shiftTimes?.start_time ||
      "",
    participant_end_time: shiftTimes?.end_time || "",
    invoice_service_type_id: sourceSplit.invoice_service_type_id ?? null,
    invoice_item_id: sourceSplit.invoice_item_id ?? null,
    custom_line_item_uuid: sourceSplit.custom_line_item_uuid ?? null,
    support_item_number: sourceSplit.support_item_number ?? "",
    support_item_name: sourceSplit.support_item_name ?? "",
    ratio: sourceSplit.ratio || "1:1",
    km_for_invoicing: resolveKmForInvoicingValue(sourceSplit.km_for_invoicing),
    default_km_code:
      sourceSplit.default_km_code !== undefined
        ? sourceSplit.default_km_code
        : null,
    claim_type: claimType,
    sta: resolveStaValue(sourceSplit.sta),
  };
};

export const applyParticipantSplitFields = (index, split, setValue) => {
  if (!setValue || split == null) return;
  const base = `advance_splits.${index}`;
  setValue(`${base}.participant`, split.participant ?? null, { shouldDirty: true });
  setValue(`${base}.participants`, split.participants ?? [], { shouldDirty: true });
  setValue(`${base}.participant_start_time`, split.participant_start_time ?? "", {
    shouldDirty: true,
  });
  setValue(`${base}.participant_end_time`, split.participant_end_time ?? "", {
    shouldDirty: true,
  });
  setValue(`${base}.invoice_service_type_id`, split.invoice_service_type_id ?? null, {
    shouldDirty: true,
  });
  setValue(`${base}.invoice_item_id`, split.invoice_item_id ?? null, {
    shouldDirty: true,
  });
  setValue(`${base}.custom_line_item_uuid`, split.custom_line_item_uuid ?? null, {
    shouldDirty: true,
  });
  setValue(`${base}.support_item_number`, split.support_item_number ?? "", {
    shouldDirty: true,
  });
  setValue(`${base}.support_item_name`, split.support_item_name ?? "", {
    shouldDirty: true,
  });
  setValue(`${base}.ratio`, split.ratio || "1:1", { shouldDirty: true });
  setValue(`${base}.km_for_invoicing`, resolveKmForInvoicingValue(split.km_for_invoicing), {
    shouldDirty: true,
  });
  setValue(
    `${base}.default_km_code`,
    split.default_km_code !== undefined ? split.default_km_code : null,
    { shouldDirty: true }
  );
  setValue(`${base}.claim_type`, split.claim_type ?? null, { shouldDirty: true });
  setValue(`${base}.sta`, resolveStaValue(split.sta), { shouldDirty: true });
};

export const getWorkerUuid = (worker) => {
  if (worker == null) return null;
  if (typeof worker === "string") return worker;
  return worker?.uuid || worker?.value || null;
};

/** Draft resume stubs use `Worker #<id>` when the worker list was not loaded yet. */
export const isStubWorkerOption = (worker) => {
  if (worker == null || typeof worker !== "object") return false;
  const label = String(worker.label || "").trim();
  if (/^Worker #\d+$/i.test(label)) return true;
  const first = String(worker.first_name || "").trim();
  const last = String(worker.last_name || "").trim();
  return first === "Worker" && /^#\d+$/.test(last);
};

/** Match workers by UUID/value or numeric id (draft stubs only have numeric id). */
export const workersMatchIdentity = (a, b) => {
  if (a == null || b == null) return false;
  const aUuid = getWorkerUuid(a);
  const bUuid = getWorkerUuid(b);
  if (aUuid && bUuid && String(aUuid) === String(bUuid)) return true;
  if (a?.id != null && b?.id != null && String(a.id) === String(b.id)) {
    return true;
  }
  return false;
};

/**
 * Prefer a full option from the loaded worker list over a draft stub / partial selection.
 */
export const resolveWorkerAgainstOptions = (worker, workerOptions = []) => {
  if (worker == null || worker === "") return null;
  const list = Array.isArray(workerOptions) ? workerOptions : [];
  if (!list.length) {
    return typeof worker === "object" ? worker : null;
  }

  const uuidFrom =
    typeof worker === "string" || typeof worker === "number"
      ? String(worker)
      : worker?.uuid || worker?.value || null;
  const idFrom =
    typeof worker === "object" && worker?.id != null ? worker.id : null;

  const matches = list.filter((w) => {
    if (!w) return false;
    if (
      uuidFrom &&
      (String(w.uuid) === String(uuidFrom) ||
        String(w.value) === String(uuidFrom) ||
        String(w.id) === String(uuidFrom))
    ) {
      return true;
    }
    if (idFrom != null && String(w.id) === String(idFrom)) return true;
    return false;
  });

  // Prefer a real loaded worker over a draft stub that may also be in the list
  const match =
    matches.find((w) => !isStubWorkerOption(w)) || matches[0] || null;

  if (match) return mapCommonWorkerToOption(match) || match;
  return typeof worker === "object" ? worker : null;
};

export const resolveWorkerSelection = (worker, workerOptions = [], userItem = null) => {
  if (worker == null || worker === "") return null;

  const uuidFrom =
    typeof worker === "string" ? worker : worker?.uuid || worker?.value || null;

  const fromOptions = resolveWorkerAgainstOptions(worker, workerOptions);

  if (fromOptions && !isStubWorkerOption(fromOptions)) return fromOptions;

  if (
    userItem &&
    uuidFrom &&
    (userItem.uuid === uuidFrom || userItem.value === uuidFrom)
  ) {
    const label =
      userItem.label ||
      `${userItem.first_name || ""} ${userItem.last_name || ""}`.trim();
    return {
      ...userItem,
      label: label || "Worker",
      value: userItem.uuid || userItem.value,
      uuid: userItem.uuid || userItem.value,
    };
  }

  if (typeof worker === "object" && worker !== null) {
    const label =
      worker.label ||
      `${worker.first_name || ""} ${worker.last_name || ""}`.trim();
    if (!label && !uuidFrom) return null;
    return {
      ...worker,
      label: label || "Worker",
      value: worker.value || worker.uuid,
      uuid: worker.uuid || worker.value,
    };
  }

  return null;
};

export const resolveWorkersList = (workerId, workerOptions = [], userItem = null) => {
  if (!workerId) return [];
  const list = Array.isArray(workerId) ? workerId.filter(Boolean) : [workerId];
  return list
    .map((item) => resolveWorkerSelection(item, workerOptions, userItem))
    .filter(Boolean);
};

export const workerNeedsResolve = (worker) => {
  if (worker == null) return false;
  if (typeof worker === "string") return true;
  // Draft stubs look "complete" (they have a label) but must be upgraded once options load
  if (isStubWorkerOption(worker)) return true;
  if (worker.label) return false;
  if (worker.first_name || worker.last_name) return false;
  return true;
};

export const getWorkerDisplayName = (worker) => {
  if (!worker) return "Worker";
  if (worker.label) return worker.label;
  const name = `${worker.first_name || ""} ${worker.last_name || ""}`.trim();
  return name || "Worker";
};

/** Selected workers who are not in the Common Workers group (informational only). */
export const resolveNonCommonSelectedWorkers = (
  selectedWorkers = [],
  groupedOptions = []
) => {
  const commonGroup = groupedOptions?.find(
    (group) => group.label === ADVANCE_COMMON_WORKER_GROUP_LABEL
  );
  const commonOptions = commonGroup?.options || [];
  if (!commonOptions.length) return [];

  const commonUuids = new Set(
    commonOptions.map((worker) => getWorkerUuid(worker)).filter(Boolean)
  );

  return (selectedWorkers || [])
    .filter((worker) => {
      const uuid = getWorkerUuid(worker);
      return uuid && !commonUuids.has(uuid);
    })
    .map((worker) => ({
      uuid: getWorkerUuid(worker),
      name: getWorkerDisplayName(worker),
    }));
};

export const isOpenSlotRow = (row) => row?.is_open_slot === true;

export const createWorkerCarerRow = (worker = null, watchFormData = {}) => ({
  worker: worker || null,
  is_open_slot: false,
  worker_start_time: watchFormData?.start_time || "",
  worker_end_time: watchFormData?.end_time || "",
  actual_start_time: "",
  actual_end_time: "",
  sleepover: false,
  active_hours: "",
});

export const createOpenSlotRow = (watchFormData = {}) => ({
  worker: null,
  is_open_slot: true,
  worker_start_time: watchFormData?.start_time || "",
  worker_end_time: watchFormData?.end_time || "",
  actual_start_time: "",
  actual_end_time: "",
  sleepover: false,
  active_hours: "",
});

export const createEmptyCarerRow = (watchFormData = {}) =>
  createWorkerCarerRow(null, watchFormData);

export const createEmptyAdvanceSplit = () => ({
  ...createEmptyParticipantSplit(),
  workers: [],
  worker_start_time: "",
  worker_end_time: "",
});

export const getSplitParticipant = (split) =>
  split?.participant || split?.participants?.[0] || null;

/**
 * When the top-level shift window changes, propagate start/end to participant
 * and worker rows. Single-segment participants get both bounds updated;
 * multi-segment participants only get the first segment start and last segment
 * end adjusted so internal split boundaries stay intact.
 */
export const syncAdvanceTimesToShiftWindow = (
  startTime,
  endTime,
  getValues,
  setValue,
  { syncStart = true, syncEnd = true } = {}
) => {
  if (!getValues || !setValue) return;
  if (syncStart && !startTime) return;
  if (syncEnd && !endTime) return;

  const splits = getValues("advance_splits") || [];
  const participantIndices = new Map();

  splits.forEach((split, index) => {
    const uuid = getParticipantUuid(getSplitParticipant(split));
    const key = uuid || `__row_${index}`;
    if (!participantIndices.has(key)) {
      participantIndices.set(key, []);
    }
    participantIndices.get(key).push(index);
  });

  participantIndices.forEach((indices) => {
    if (syncStart) {
      setValue(
        `advance_splits.${indices[0]}.participant_start_time`,
        startTime,
        { shouldDirty: true }
      );
    }
    if (syncEnd) {
      setValue(
        `advance_splits.${indices[indices.length - 1]}.participant_end_time`,
        endTime,
        { shouldDirty: true }
      );
    }
  });

  const carers = getValues("advance_carers") || [];
  carers.forEach((_, index) => {
    if (syncStart) {
      setValue(`advance_carers.${index}.worker_start_time`, startTime, {
        shouldDirty: true,
      });
    }
    if (syncEnd) {
      setValue(`advance_carers.${index}.worker_end_time`, endTime, {
        shouldDirty: true,
      });
    }
  });
};

/** STA on: shift window and all participant/worker segment times become 24 hours (00:00–00:00). */
export const applyStaFullDayShiftTimes = (setValue, getValues) => {
  if (!setValue || !getValues) return;
  const staTime = ADVANCE_STA_FULL_DAY_TIME;
  setValue("start_time", staTime, { shouldDirty: true });
  setValue("end_time", staTime, { shouldDirty: true });
  syncAdvanceTimesToShiftWindow(staTime, staTime, getValues, setValue);
};

/** True when this split index is the last segment for its participant. */
export const isLastSegmentForParticipant = (index, allSplits = []) => {
  const split = allSplits[index];
  const pt = getSplitParticipant(split);
  const uuid = getParticipantUuid(pt);
  if (!uuid) return false;

  for (let i = allSplits.length - 1; i >= 0; i--) {
    if (getParticipantUuid(getSplitParticipant(allSplits[i])) === uuid) {
      return i === index;
    }
  }
  return false;
};

export const findShiftInstructionIndex = (participant, shiftInstructions = []) => {
  if (!participant || !shiftInstructions?.length) return -1;

  const ptId = participant?.id;
  const uuid = getParticipantUuid(participant);

  return shiftInstructions.findIndex(
    (inst) =>
      (ptId != null && (inst.id === ptId || String(inst.id) === String(ptId))) ||
      (uuid && (inst.uuid === uuid || inst.pt_id === uuid))
  );
};

/** Precompute per-segment UI metadata once per splits array (avoids N× watch in render loops). */
export const buildAdvanceSplitCardMeta = (
  allSplits = [],
  {
    homeManagementRequiresParticipant = false,
    shiftInstructions = [],
    showApplyKmToInvoice = false,
  } = {}
) => {
  const uniqueUuids = new Set();
  allSplits.forEach((s) => {
    const uuid = getParticipantUuid(getSplitParticipant(s));
    if (uuid) uniqueUuids.add(uuid);
  });
  const uniqueParticipantCount = uniqueUuids.size;
  const segmentCountByUuid = {};

  return allSplits.map((split, index) => {
    const participant = getSplitParticipant(split);
    const uuid = getParticipantUuid(participant);

    if (uuid) {
      segmentCountByUuid[uuid] = (segmentCountByUuid[uuid] || 0) + 1;
    }
    const segmentIndex = uuid ? segmentCountByUuid[uuid] : 0;
    const segmentLabel =
      segmentIndex > 1 ? `(Breakdown ${segmentIndex})` : null;

    const isLastSegment = isLastSegmentForParticipant(index, allSplits);
    const instructionIndex = findShiftInstructionIndex(
      participant,
      shiftInstructions
    );

    let canRemove = true;
    if (homeManagementRequiresParticipant && uniqueParticipantCount <= 1 && uuid) {
      const segmentsForParticipant = allSplits.filter(
        (s) => getParticipantUuid(getSplitParticipant(s)) === uuid
      );
      if (segmentsForParticipant.length <= 1) {
        canRemove = false;
      }
    }

    return {
      segmentLabel,
      canRemove,
      showShiftInstruction: isLastSegment,
      showKmToggle: showApplyKmToInvoice && isLastSegment,
      applyKmValue: getParticipantKmForInvoicing(uuid, allSplits),
      defaultKmCode: getParticipantDefaultKmCode(uuid, allSplits),
      showKmCodeDropdown:
        showApplyKmToInvoice &&
        isLastSegment &&
        getParticipantKmForInvoicing(uuid, allSplits),
      showStaToggle: isLastSegment,
      participantStaValue: getParticipantSta(uuid, allSplits),
      instructionIndex,
    };
  });
};

/** Fingerprint advance_splits for memoized render metadata. */
export const getAdvanceSplitsFingerprint = (allSplits = []) =>
  allSplits
    .map((s, i) => {
      const p = getSplitParticipant(s);
      return [
        i,
        getParticipantUuid(p),
        s?.participant_start_time,
        s?.participant_end_time,
        s?.km_for_invoicing,
        s?.default_km_code,
        s?.sta,
      ].join(":");
    })
    .join("|");

const isCustomLineItemOption = (option) =>
  option?.__type === "custom" || option?.is_custom || option?.isCustom;

/** Invoice item id written by the custom-line select is a uuid, not a catalogue id. */
const customUuidFromSplit = (split) => {
  const direct = split?.custom_line_item_uuid;
  if (direct) return String(direct).replace(/^custom:/, "");
  const raw = split?.invoice_item_id;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("custom:")) return trimmed.slice(7);
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      trimmed
    )
  ) {
    return trimmed;
  }
  return null;
};

/**
 * Keep NDIS / custom / STA dropdown selection when options reload (e.g. after Split).
 * keepSavedWhenMissing is only for the shift edit form. Timesheet breakdowns rely on
 * the default: once a catalogue has loaded, an unmatched row stays empty.
 */
export const buildNdisSelectedOption = (
  split,
  ndisOptions = [],
  { keepSavedWhenMissing = false } = {}
) => {
  const customUuid = customUuidFromSplit(split);
  if (customUuid) {
    const customValue = `custom:${customUuid}`;
    const customFound = ndisOptions.find(
      (x) =>
        String(x.value) === customValue ||
        String(x.value) === String(customUuid) ||
        String(x.uuid) === String(customUuid)
    );
    if (customFound) return customFound;
    const name = split.support_item_name || "";
    const number = split.support_item_number || "";
    if (name || number) {
      return {
        label: name || number,
        value: customValue,
        uuid: customUuid,
        number,
        name: name || number,
        isCustom: true,
        __type: "custom",
        __orphan: true,
      };
    }
  }

  const itemKey = split?.invoice_item_id ?? split?.support_item_number;
  // On edit, invoice_item_id comes back as a numeric PriceCatalog id (the create
  // flow used the support-item number), so it may not match the freshly fetched
  // option value. Match the support-item number too so the saved line item still
  // resolves to its option and displays as selected.
  const numberKey = split?.support_item_number ?? null;
  if (itemKey == null && numberKey == null && !(keepSavedWhenMissing && customUuid)) {
    return null;
  }

  const found = ndisOptions.find(
    (x) =>
      x?.__type !== "custom" &&
      ((itemKey != null &&
        (String(x.value) === String(itemKey) ||
          String(x.number) === String(itemKey))) ||
        (numberKey != null && String(x.number) === String(numberKey)))
  );
  if (found) return found;

  // Aged Care / Private / Other rows saved before custom_line_item_id was
  // persisted still have the unique code. Match that code to a custom option
  // once the NDIS catalogue lookup has missed.
  if (numberKey != null && numberKey !== "") {
    const customByNumber = ndisOptions.find(
      (x) =>
        isCustomLineItemOption(x) &&
        x.number != null &&
        String(x.number) === String(numberKey)
    );
    if (customByNumber) return customByNumber;
  }

  // Options loaded but selection is not valid for this category — show empty.
  // Shift edit opts in so a line item saved under the previous care sector
  // stays visible. Timesheets keep the empty result.
  if (ndisOptions.length > 0 && !keepSavedWhenMissing) {
    return null;
  }

  const name = split.support_item_name || "";
  const number = split.support_item_number || "";
  if (name || number) {
    return {
      label: name || number,
      value: customUuid ? `custom:${customUuid}` : itemKey,
      uuid: customUuid || undefined,
      number,
      name: name || number,
      ...(customUuid
        ? { isCustom: true, __type: "custom", __orphan: true }
        : {}),
    };
  }

  if (itemKey == null && !customUuid) return null;

  return {
    label: "Selected line item",
    value: customUuid ? `custom:${customUuid}` : itemKey,
    uuid: customUuid || undefined,
    number: "",
    name: "",
    ...(customUuid
      ? { isCustom: true, __type: "custom", __orphan: true }
      : {}),
  };
};

/** Clear line item and claim type when STA toggles so user picks fresh values. */
export const clearSplitLineItemAndClaimTypeOnSplit = (index, split, setValue) => {
  if (!setValue) return;
  const hasLineItem =
    split?.invoice_item_id ||
    split?.custom_line_item_uuid ||
    split?.support_item_number ||
    split?.support_item_name;
  const hasClaimType = !!split?.claim_type;
  if (!hasLineItem && !hasClaimType) return;
  const base = `advance_splits.${index}`;
  if (hasLineItem) {
    setValue(`${base}.invoice_item_id`, null, { shouldDirty: true });
    setValue(`${base}.custom_line_item_uuid`, null, { shouldDirty: true });
    setValue(`${base}.support_item_number`, "", { shouldDirty: true });
    setValue(`${base}.support_item_name`, "", { shouldDirty: true });
  }
  if (hasClaimType) {
    setValue(`${base}.claim_type`, DEFAULT_ADVANCE_CLAIM_TYPE, { shouldDirty: true });
  }
};

const ADVANCE_TIME_PARSE_FORMATS = ["HH:mm", "hh:mm A", "h:mm A"];

const parseShiftCalendarDay = (shiftDate) => {
  if (!shiftDate) return null;
  const strict = moment(shiftDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true);
  if (strict.isValid()) return strict.startOf("day");
  const loose = moment(shiftDate);
  return loose.isValid() ? loose.startOf("day") : null;
};

const parseClockTime = (timeStr) => {
  if (timeStr == null || timeStr === "") return null;
  const parsed = moment(timeStr, ADVANCE_TIME_PARSE_FORMATS, true);
  if (parsed.isValid()) return parsed;
  const timeParts = String(timeStr).match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!timeParts) return null;
  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const period = timeParts[3]?.toUpperCase();
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;
  return moment({ hour: hours, minute: minutes });
};

/**
 * Normalise any accepted clock-time string (e.g. "9:00 AM", "09:00") to the
 * 24-hour "HH:mm" value a native <input type="time"> requires. Returns "" when
 * the value can't be parsed so the input renders empty rather than broken.
 */
export const toTimeInputValue = (value) => {
  const parsed = parseClockTime(value);
  return parsed ? parsed.format("HH:mm") : "";
};

/** True when end clock time is on or before start (shift spans midnight). */
export const isAdvanceTimeRangeOvernight = (startTime, endTime) => {
  const time1 = parseClockTime(startTime);
  const time2 = parseClockTime(endTime);
  if (!time1 || !time2) return false;
  return time2.diff(time1, "minutes") <= 0;
};

/** Combine shift date + wall-clock time as `YYYY-MM-DD HH:mm:ss` (no UTC conversion). */
export const formatAdvanceShiftDateTime = (
  shiftDate,
  timeStr,
  { addDay = false } = {}
) => {
  const dt = parseAdvanceWallClockMoment(shiftDate, timeStr);
  if (!dt) return "";
  if (addDay) dt.add(1, "day");
  return dt.format("YYYY-MM-DD HH:mm:ss");
};

const parseAdvanceWallClockMoment = (shiftDate, timeStr) => {
  const base = parseShiftCalendarDay(shiftDate);
  const clock = parseClockTime(timeStr);
  if (!base || !clock) return null;
  return base
    .clone()
    .hour(clock.hour())
    .minute(clock.minute())
    .second(0)
    .millisecond(0);
};

const getAdvanceClockOffsetMinutes = (fromClock, toClock) => {
  const from = parseClockTime(fromClock);
  const to = parseClockTime(toClock);
  if (!from || !to) return null;
  let diff = to.diff(from, "minutes");
  if (diff < 0) diff += 24 * 60;
  return diff;
};

/**
 * True only for the overnight morning continuation (e.g. 12am–6am on a 6pm–8am
 * shift). Same-day windows, STA start-of-day clocks, and out-of-window times
 * are left on the shift date so other create/update paths stay unchanged.
 */
const shouldDateClockOnNextDay = (
  windowStartClock,
  windowEndClock,
  clock
) => {
  if (!isAdvanceTimeRangeOvernight(windowStartClock, windowEndClock)) {
    return false;
  }

  const windowStart = parseAdvanceWallClockMoment("2000-01-01", windowStartClock);
  const clockDt = parseAdvanceWallClockMoment("2000-01-01", clock);
  if (!windowStart || !clockDt || !clockDt.isBefore(windowStart)) {
    return false;
  }

  const duration = isAdvanceSameClockTimeFullDay(windowStartClock, windowEndClock)
    ? 24 * 60
    : getAdvanceClockOffsetMinutes(windowStartClock, windowEndClock);
  const offset = getAdvanceClockOffsetMinutes(windowStartClock, clock);
  if (duration == null || offset == null) return false;

  return offset > 0 && offset < duration;
};

/**
 * Date a start/end clock onto the shift timeline as `YYYY-MM-DD HH:mm:ss`.
 *
 * Overnight windows (6pm–8am, 24h same-clock, STA): clocks strictly before the
 * shift start *and still inside that window* land on the next calendar day.
 * End is always after start (same-clock ranges stay 24h). Same-day windows are
 * unchanged.
 */
export const buildAdvanceRangeDateTimes = (
  shiftDate,
  startClock,
  endClock,
  { windowStartClock = startClock, windowEndClock = endClock } = {}
) => {
  const startBase = parseAdvanceWallClockMoment(shiftDate, startClock);
  const endBase = parseAdvanceWallClockMoment(shiftDate, endClock);
  if (!startBase || !endBase) {
    return { start_time: "", end_time: "" };
  }

  let startDt = startBase.clone();
  if (
    shouldDateClockOnNextDay(windowStartClock, windowEndClock, startClock)
  ) {
    startDt.add(1, "day");
  }

  let endDt = endBase.clone();
  if (!endDt.isAfter(startDt)) {
    endDt.add(1, "day");
  }

  return {
    start_time: startDt.format("YYYY-MM-DD HH:mm:ss"),
    end_time: endDt.format("YYYY-MM-DD HH:mm:ss"),
  };
};

const buildAdvanceStartDateTime = (shiftDate, timeStr) =>
  formatAdvanceShiftDateTime(shiftDate, timeStr, { addDay: false });

const buildAdvanceEndDateTime = (shiftDate, startTime, endTime) =>
  buildAdvanceRangeDateTimes(shiftDate, startTime, endTime).end_time;

/** Backward-compatible wrapper; prefer buildAdvanceStartDateTime / buildAdvanceEndDateTime. */
const buildAdvanceDateTime = (shiftDate, timeStr, refStart) => {
  if (refStart != null && String(refStart) !== String(timeStr)) {
    return buildAdvanceEndDateTime(shiftDate, refStart, timeStr);
  }
  return buildAdvanceStartDateTime(shiftDate, timeStr);
};

/**
 * Normalize GET/edit form.shift_tasks rows (collapsed "all" or single worker).
 * @see V5_SHIFT_TASK_WORKER_ASSIGNEE_FRONTEND.md
 */
export const normalizeAdvanceShiftTaskForForm = (task) => {
  if (!task || typeof task !== "object") return task;

  const assignToRaw = task.assign_to ?? task.assignTo ?? null;
  if (assignToRaw === "all") {
    return {
      ...task,
      assign_to: "all",
      worker_id: null,
      assignee_id: null,
    };
  }

  const workerIdRaw = task.worker_id ?? task.assignee_id ?? null;
  if (
    assignToRaw === "worker" ||
    (workerIdRaw != null && workerIdRaw !== "")
  ) {
    const workerId = Number(workerIdRaw);
    if (!Number.isNaN(workerId)) {
      return {
        ...task,
        assign_to: "worker",
        worker_id: workerId,
        assignee_id: workerId,
      };
    }
  }

  return {
    ...task,
    assign_to: "all",
    worker_id: null,
    assignee_id: null,
  };
};

/**
 * V5 advance shift_tasks payload.
 * Backend expands assign_to:"all" into one row per filled worker.
 * @see V5_SHIFT_TASK_WORKER_ASSIGNEE_FRONTEND.md
 */
export const prepareAdvanceShiftTasks = (tasks = []) =>
  (tasks || [])
    .filter((row) => row?.task && String(row.task).trim() !== "")
    .map((row) => {
      const normalized = normalizeAdvanceShiftTaskForForm(row);
      const assignTo = normalized.assign_to === "worker" ? "worker" : "all";
      const workerId =
        assignTo === "worker" && normalized.worker_id != null
          ? Number(normalized.worker_id)
          : null;

      // Every row id this logical task already owns. Sending them lets the backend
      // update the existing rows in place (keeping ids + worker completion) instead
      // of deleting and recreating them whenever the text or mandatory flag changes.
      const ids = Array.from(
        new Set(
          [...(Array.isArray(row.ids) ? row.ids : []), row.id]
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      return {
        ...(row.id ? { id: Number(row.id) } : {}),
        ...(ids.length > 0 ? { ids } : {}),
        task: String(row.task).trim(),
        pt_id: row.pt_id ?? null,
        participant_task_id: row.participant_task_id ?? null,
        is_mandatory:
          row?.is_mandatory === true ||
          row?.is_mandatory === 1 ||
          row?.is_mandatory === "1",
        assign_to: assignTo,
        ...(assignTo === "worker" && workerId && !Number.isNaN(workerId)
          ? { worker_id: workerId }
          : {}),
      };
    });

/**
 * Per-participant shift instructions for V5 advance create/update.
 * Backend expects `{ pt_id, shift_instruction }` rows (StoreV5AdvanceShiftRequest).
 */
export const prepareAdvanceShiftInstructions = (shiftInstructions = []) => {
  if (!shiftInstructions?.length) return null;
  return shiftInstructions.map((item) => ({
    pt_id: item?.id,
    shift_instruction: item?.shift_instruction ? item.shift_instruction : null,
  }));
};

/** V5 POST /sp/rostering/shift/advance — matches backend StoreV5AdvanceShiftRequest shape. */
export const buildAdvanceShiftCreatePayload = ({
  watchFormData = {},
  advanceSplits = [],
  advanceCarers = [],
  shiftLocation = null,
  participantOptions = [],
  workerOptions = [],
  notes = [],
  tasks = [],
  shiftInstructions = [],
  timezone = "Australia/Sydney",
}) => {
  const shiftDateRaw = watchFormData?.shift_date;
  const shiftDate = shiftDateRaw
    ? moment(shiftDateRaw, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true).isValid()
      ? moment(shiftDateRaw, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true).format(
          "YYYY-MM-DD"
        )
      : moment(shiftDateRaw).format("YYYY-MM-DD")
    : "";

  const shiftWindowTimes = buildAdvanceRangeDateTimes(
    shiftDateRaw,
    watchFormData?.start_time,
    watchFormData?.end_time
  );
  const startTime = shiftWindowTimes.start_time;
  const endTime = shiftWindowTimes.end_time;

  const isNextDayShift = isAdvanceTimeRangeOvernight(
    watchFormData?.start_time,
    watchFormData?.end_time
  );

  const isOpenShift = parseBooleanVal(watchFormData?.advance_open_shift);

  const participantIds = resolveAdvanceParticipantIds(
    watchFormData?.advance_selected_participants || [],
    participantOptions
  );

  const splitsByParticipant = new Map();
  const fullDaySeenByParticipant = new Set();
  const shiftStartClock = watchFormData?.start_time;
  const shiftEndClock = watchFormData?.end_time;
  (advanceSplits || []).forEach((split) => {
    const pt = getSplitParticipant(split);
    const participantId = getParticipantId(pt, participantOptions);
    if (!participantId) return;

    const ptKey = String(participantId);
    if (
      isAdvanceUnallocatedSameClockSegment(
        split.participant_start_time,
        split.participant_end_time,
        shiftStartClock,
        shiftEndClock,
        { alreadyCountedFullDay: fullDaySeenByParticipant.has(ptKey) }
      )
    ) {
      return;
    }
    if (
      isAdvanceSameClockTimeFullDay(
        split.participant_start_time,
        split.participant_end_time
      )
    ) {
      fullDaySeenByParticipant.add(ptKey);
    }

    const segmentTimes = buildAdvanceRangeDateTimes(
      shiftDateRaw,
      split.participant_start_time,
      split.participant_end_time,
      {
        windowStartClock: shiftStartClock,
        windowEndClock: shiftEndClock,
      }
    );

    const segment = {
      start_time: segmentTimes.start_time,
      end_time: segmentTimes.end_time,
      service_type_id: split.invoice_service_type_id ?? null,
      invoice_item_id: split.custom_line_item_uuid
        ? null
        : split.invoice_item_id ?? null,
      custom_line_item_uuid: split.custom_line_item_uuid || null,
      ratio: split.ratio || "1:1",
      claim_type: split.claim_type ?? null,
      is_sleepover: resolveAdvanceSplitSleepover(split),
      km_for_invoicing: resolveKmForInvoicingValue(split.km_for_invoicing),
      apply_km: resolveKmForInvoicingValue(split.km_for_invoicing),
      default_km_code:
        split.default_km_code !== undefined ? split.default_km_code : null,
    };

    if (split.support_item_number) {
      segment.support_item_number = split.support_item_number;
    }
    if (split.support_item_name) {
      segment.support_item_name = split.support_item_name;
    }

    if (!splitsByParticipant.has(participantId)) {
      splitsByParticipant.set(participantId, []);
    }
    splitsByParticipant.get(participantId).push(segment);
  });

  const participant_splits = Array.from(splitsByParticipant.entries()).map(
    ([participant_id, splits]) => ({
      participant_id,
      splits,
    })
  );

  let workerCarerRows = (advanceCarers || []).filter((row) => !isOpenSlotRow(row));
  const openSlotRows = (advanceCarers || []).filter(isOpenSlotRow);
  if (!isOpenShift && workerCarerRows.length === 0 && openSlotRows.length === 0) {
    const shiftTimes = {
      start_time: watchFormData?.start_time,
      end_time: watchFormData?.end_time,
    };
    workerCarerRows = (watchFormData?.advance_selected_workers || []).map((w) =>
      createWorkerCarerRow(w, shiftTimes)
    );
  }

  const workers = [
    ...workerCarerRows
      .map((row) => {
        const workerId = getWorkerId(row?.worker, workerOptions) ?? row?.worker_id;
        if (!workerId) return null;
        const isWorkerSleepover = parseBooleanVal(row?.sleepover) || false;
        const workerTimes = buildAdvanceRangeDateTimes(
          shiftDateRaw,
          row.worker_start_time,
          row.worker_end_time,
          {
            windowStartClock: shiftStartClock,
            windowEndClock: shiftEndClock,
          }
        );
        return {
          worker_id: workerId,
          start_time: workerTimes.start_time,
          end_time: workerTimes.end_time,
          sleepover: isWorkerSleepover,
          ...(isWorkerSleepover && row?.active_hours
            ? { active_hours: String(row.active_hours).trim() }
            : {}),
        };
      })
      .filter(Boolean),
    ...openSlotRows.map((row) => {
      const slotTimes = buildAdvanceRangeDateTimes(
        shiftDateRaw,
        row.worker_start_time,
        row.worker_end_time,
        {
          windowStartClock: shiftStartClock,
          windowEndClock: shiftEndClock,
        }
      );
      return {
        worker_id: null,
        is_open: true,
        start_time: slotTimes.start_time,
        end_time: slotTimes.end_time,
      };
    }),
  ];

  const isSta =
    resolveStaValue(watchFormData?.sta) ||
    (advanceSplits || []).some((split) => resolveStaValue(split?.sta));

  const isSleepover = parseBooleanVal(watchFormData?.sleepover);

  // Prefer participant-level travel so multi-segment participants stay consistent.
  const participant_travel = buildParticipantTravelFromSplits(
    advanceSplits,
    participantOptions
  );

  // Keep segment flags in sync with the participant-level travel row.
  const travelByParticipantId = new Map(
    participant_travel.map((row) => [String(row.participant_id), row])
  );
  const syncedParticipantSplits = participant_splits.map((group) => {
    const travel = travelByParticipantId.get(String(group.participant_id));
    if (!travel) return group;
    return {
      ...group,
      splits: (group.splits || []).map((segment) => ({
        ...segment,
        km_for_invoicing: travel.km_for_invoicing,
        apply_km: travel.km_for_invoicing,
        default_km_code: travel.default_km_code,
      })),
    };
  });

  const payload = {
    shift_date: shiftDate,
    start_time: startTime,
    end_time: endTime,
    timezone,
    is_open_shift: isOpenShift,
    participant_ids: participantIds,
    location_tracking: parseBooleanVal(watchFormData?.location_tracking),
    is_signature: parseBooleanVal(watchFormData?.is_signature),
    is_sta: isSta,
    is_radius: parseBooleanVal(watchFormData?.is_radius),
    sleepover: isSleepover,
    is_next_day_shift: isNextDayShift,
    workers,
    participant_splits: syncedParticipantSplits,
    participant_travel,
  };

  if (watchFormData?.advance_sil_home_enabled && watchFormData?.advance_sil_home_id) {
    payload.sil_home_id = watchFormData.advance_sil_home_id;
  }

  if (
    shiftLocation?.street &&
    shiftLocation?.suburb &&
    shiftLocation?.state &&
    shiftLocation?.postcode
  ) {
    payload.shift_location = {
      street: shiftLocation.street,
      suburb: shiftLocation.suburb,
      state: shiftLocation.state,
      state_name: shiftLocation.state_name || "",
      postcode: shiftLocation.postcode,
      lat: shiftLocation.lat ?? null,
      lng: shiftLocation.lng ?? null,
    };
  }

  // Flat draft/create notes only — never POST V5 worker-grouped timesheet notes
  // (those are written via timesheet detail / worker end-shift).
  const noteText = (notes || [])
    .map((item) => {
      if (item == null || typeof item !== "object") return null;
      if (Array.isArray(item.notes)) return null;
      const text = item.notes;
      return typeof text === "string" && text.trim() ? text.trim() : null;
    })
    .filter(Boolean)
    .join("; ");
  if (noteText) {
    payload.note = noteText;
  }

  // Always include (even []) so update can clear all tasks; omit would leave existing rows.
  // @see V5_SHIFT_TASK_WORKER_ASSIGNEE_FRONTEND — shift_tasks: [] deletes all.
  payload.shift_tasks = prepareAdvanceShiftTasks(tasks);

  const preparedShiftInstructions =
    prepareAdvanceShiftInstructions(shiftInstructions);
  if (preparedShiftInstructions?.length > 0) {
    payload.shift_instructions = preparedShiftInstructions;
  }

  return payload;
};

/** Flatten V5 advance create API response for calendar reducer + schedule display. */
export const normalizeAdvanceShiftCreateResponse = (response) => {
  const envelope = response?.data ?? response;
  const result =
    envelope?.advance_shift ?? envelope?.data ?? envelope;
  const shift = result?.shift ?? result;
  if (!shift || typeof shift !== "object") {
    return result;
  }

  const participants =
    shift.participants || result?.participants || [];
  const loadedAssignments =
    shift?.worker_assignments ??
    shift?.workerAssignments ??
    result?.worker_assignments ??
    shift?.workers ??
    [];
  const assignments = Array.isArray(loadedAssignments)
    ? loadedAssignments
    : loadedAssignments?.data || [];

  const workers = assignments
    .map((assignment) => ({
      assignment_id: assignment?.id ?? assignment?.assignment_id ?? null,
      is_open: parseBooleanVal(assignment?.is_open),
      status: assignment?.status ?? "accepted",
      progress: assignment?.progress ?? "created",
      start_time: assignment?.start_time ?? null,
      end_time: assignment?.end_time ?? null,
      sleepover: parseBooleanVal(
        assignment?.sleepover ?? assignment?.is_sleepover
      ),
      worker: assignment?.worker ?? null,
      worker_id: assignment?.worker_id ?? assignment?.worker?.id ?? null,
    }))
    .filter(
      (row) =>
        row.worker ||
        row.assignment_id != null ||
        row.worker_id != null
    );

  const isOpenShift = parseBooleanVal(
    shift.is_open_shift ?? result?.is_open_shift
  );
  const participantCount = participants.length;
  const isGroupShift =
    participantCount > 1 || parseBooleanVal(shift.is_group_shift);
  const firstWorker = workers.find((w) => w?.worker)?.worker ?? null;
  const mainWorkerId =
    shift.main_worker_id ?? firstWorker?.id ?? null;
  const splitShifts =
    shift.split_shifts ||
    shift.splitShifts ||
    result?.billing_splits ||
    [];

  return {
    ...shift,
    participants,
    split_shifts: splitShifts,
    splitShifts,
    is_multi_assignment: true,
    is_open_shift: isOpenShift,
    is_group_shift: isGroupShift,
    open_group_shift:
      isOpenShift &&
      (parseBooleanVal(shift.open_group_shift) || participantCount > 1),
    workers,
    worker: shift.worker ?? firstWorker ?? null,
    worker_id:
      shift.worker_id ?? mainWorkerId ?? firstWorker?.id ?? null,
    main_worker_id: shift.main_worker_id ?? mainWorkerId,
    pt_id: shift.pt_id ?? participants[0]?.id ?? null,
  };
};
